/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Game from '../../Game/Game';
import { StateID } from '../../StateManager/StateID';
import StateReference from '../../StateManager/StateReference';
import InjectableComponent from '../../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableBooleanComponent from '../../Utility/Component/Observable/ObservableBooleanComponent';
import ComponentBase from '../../Utility/ComponentBase';
import IObservable from '../../Utility/Observable/IObservable';
import Reel from '../Reel';
import ReelManager from '../ReelManager';
import IQuickStop from './IQuickStop';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Component responsible for QuickStopping a [[ReelManager]] component.
 */
@cc._decorator.ccclass
@cc._decorator.requireComponent(ReelManager)
export default class QuickStopComponent extends ComponentBase implements IQuickStop
{
    /** The state on which quickstop is enabled. */
    @property({ type: [StateReference], visible: true })
    protected quickstopStates: StateReference[] =
    [
        new StateReference(StateID.Request),
        new StateReference(StateID.Spinning)
    ];

    /** Whether game should quick spin or not. */
    @property({ type: InjectableComponent, visible: true })
    protected isTurboModeRef: InjectableComponent<IObservable<boolean>>
        = new InjectableComponent<IObservable<boolean>>(ReferenceGroup.MainGame, 'AutoPlay.IsTurboMode');

    /** @hidden */
    private _gameRef: InjectableComponent<Game> = new InjectableComponent<Game>();

    /** Required [[ReelManager]] component. */
    protected reelManager: ReelManager = null;

    /** Flag indicating a quick stop is queued. */
    protected shouldQuickStop: boolean = false;

    /** @hidden */
    protected hasExecuted: boolean = false;
    /** True when QuickStop has been executed since last reset. */
    public get HasExecuted(): boolean { return this.hasExecuted; }

    /** True if [[ReelManager]] has spun for minmum time from [[GameSetup]]. */
    protected minSpinTimeComplete: boolean = false;

    /** [[ReelManager]] reeels. */
    protected get reels(): Reel[] { return this.reelManager.Reels; }

    /** @hidden */
    protected setContexts(): void
    {
        this._gameRef.SetContext(Game);
        this.isTurboModeRef.SetContext(ObservableBooleanComponent);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.reelManager = this.getComponent(ReelManager);

        this.configureListeners();
    }

    /** @hidden */
    protected configureListeners(): void
    {
        this.reelManager.node.on(ReelManager.OnSpinStart, () => this.Reset());
        this.reelManager.node.on(ReelManager.OnMinSpinTimeComplete, () => this.setMinTimeComplete());
    }

    /** @hidden */
    protected onDestroy(): void
    {
        if (!this.reelManager || !this.reelManager.node) { return; }
        this.reelManager.node.off(ReelManager.OnSpinStart, () => this.Reset());
        this.reelManager.node.off(ReelManager.OnMinSpinTimeComplete, () => this.setMinTimeComplete());
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this._gameRef.WithValue(game =>
            {
                game.AdvancePlayEventDispatcher
                    .on(Game.AdvancePlayEventName, this.advancePlayEventHandler.bind(this));
            });

        if (this.reelManager.GameSetup)
        {
            this.reelManager.GameSetup.OnSetupReceived(() =>
            {
                this.enabled = this.reelManager.GameSetup.QuickStopPermitted;
            });
        }
    }

    /** @hidden */
    protected update(dt: number): void
    {
        super.update(dt);

        if (this.shouldQuickStop
                && !this.reelManager.Reels.some(reel => reel.MovedSymbols < reel.VisibleSymbols.length * 5)
                && this.minSpinTimeComplete
                && this.reelManager.CanQuickStop) { this.quickStopHandler(); }
    }

    /** Schedule a quickstop. */
    public Execute(): void
    {
        if (this.HasExecuted || !this.reelManager.CanQuickStop)
        {
            this.shouldQuickStop = false;
            return;
        }

        this.shouldQuickStop = true;
    }

    /** Resets values in anticipation of new quickstop. */
    public Reset(): void
    {
        if (!this.enabled) { return; }

        this.minSpinTimeComplete = false;
        this.shouldQuickStop = this.isTurboModeRef.Value && this.isTurboModeRef.Value.Value;

        this.hasExecuted = false;
    }

    /** Overrideable handler for quickstopping. */
    protected quickStopHandler(): void
    {
        for (let i: number = 0; i < this.reels.length - 1; i++)
        {
            this.reels[i].OnStoppedOnce(() => this.reels[i + 1].QuickStop());
        }

        this.reels[0].QuickStop();

        this.shouldQuickStop = this.isTurboModeRef.Value && this.isTurboModeRef.Value.Value;
        this.hasExecuted = true;
    }

    /** @hidden */
    private advancePlayEventHandler = (event: cc.Event.EventCustom): void =>
    {
        if (this.quickstopStates.find(StateReference.IsCurrentFilter))
        {
            this.Execute();
        }
    }

    /** @hidden */
    private setMinTimeComplete(): void
    {
        this.minSpinTimeComplete = true;
    }
}
